import React, { useContext, useEffect, useState } from 'react';
import { Button, Dialog } from '@mui/material';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { FaPaperPlane } from 'react-icons/fa';
import { IManageAssignments } from './IManageAssignments';
import { Field } from '@sitecore-jss/sitecore-jss-react';
import { getAPICall, postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import './index.scss';

export type ManageAssignmentsProps = {
  props: {
    open: boolean;
    closeManageAssignmentsPopup: any;
    EmailSentHeaderText: Field<string>;
    EmailHeaderText: Field<string>;
    ActionsHeaderText: Field<string>;
    StatusTooltipText: Field<string>;
    ActionTooltipText: Field<string>;
    ResendButtonText: Field<string>;
    ReassignButtonText: Field<string>;
    CancelButtonText: Field<string>;
    SaveAndSendLink: Field<string>;
    EmailErrorMessage: Field<string>;
    orderId: string;
    lineItemId: string;
  };
};

type DialogTitleProps = {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
};

const StyledDialogTitleRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  position: 'relative',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

export const DialogTitle = ({ children, onClose, id }: DialogTitleProps) => (
  <StyledDialogTitleRoot id={id}>
    <Typography variant="h6">{children}</Typography>
    {onClose && (
      <CloseButton aria-label="close" onClick={onClose}>
        <CloseIcon />
      </CloseButton>
    )}
  </StyledDialogTitleRoot>
);

export const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

export const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 700,
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    color: theme.palette.common.white,
  },
  '&.MuiTableCell-body': {
    fontSize: 14,
  },
}));

const ManageAssignments = ({ props }: ManageAssignmentsProps): React.JSX.Element => {
  const [ID, setID] = useState('');
  const [manageAssignmentsDetails, setManageAssignmentsDetails] = useState(
    Array<IManageAssignments>()
  );
  const [loadStatus, setLoadStatus] = useState<boolean>(false);
  const [resendStatus, setResendStatus] = useState<boolean>(false);
  const { state } = useContext(APIWebstoreContext);

  useEffect(() => {
    if (props.open) {
    }
    getManageAssignmentsDetails();
  }, [props.open]);

  function getManageAssignmentsDetails() {
    if ((props?.orderId !== '' || undefined) && (props?.lineItemId !== '' || undefined)) {
      let apiUrl = `${process.env.REACT_APP_CART_URL}/cartorder/${props?.orderId}/getdigitaldeliveryitemsstatus/${props?.lineItemId}`;
      getAPICall(apiUrl).then(function (response: any) {
        if (response && response.status === 200) {
          setManageAssignmentsDetails(response.data);
        }
      });
    }
  }

  const handleClose = () => {
    props.closeManageAssignmentsPopup();
    setLoadStatus(false);
    setResendStatus(false);
  };

  function updateEmail(Id: string) {
    setID(Id);
    if (props?.orderId !== undefined && Id !== undefined) {
      postAPICall(
        `${process.env.REACT_APP_CART_URL}/cartorder/${props?.orderId}/resenddigitaldelivery/${props?.lineItemId}/request/${Id}`,
        {}
      )
        .then(function (response: any) {
          if (response && response.status === 200) {
            setResendStatus(true);
            getManageAssignmentsDetails();
          } else {
            setResendStatus(false);
          }
        })
        .catch((error) => {
          setResendStatus(false);
        })
        .finally(() => {
          setLoadStatus(true);
        });
    }
  }

  return (
    <div className="manageAssignments">
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={props.open}
        className="manageAssignmentsDialog"
        BackdropProps={{ invisible: true }}
        PaperProps={{
          style: {
            minHeight: '20vh',
            minWidth: '55vw',
            borderRadius: '5px',
          },
        }}
      >
        <DialogTitle id="manageAssignmentsTitle" onClose={handleClose}>
          <div className="manageAssignmentsHeader">
            <div className="headerColumn statusColumn">
              <Typography className="dialogHeading">{props?.EmailSentHeaderText?.value}</Typography>
              <CustomTooltip
                fields={{
                  'Tooltip Text': props.StatusTooltipText,
                }}
              />
            </div>
            <div className="headerColumn emailColumn">
              <Typography className="dialogHeading">{props?.EmailHeaderText?.value}</Typography>
            </div>
            <div className="headerColumn actionsColumn">
              <Typography className="dialogHeading">{props?.ActionsHeaderText?.value}</Typography>
              <CustomTooltip
                fields={{
                  'Tooltip Text': props.ActionTooltipText,
                }}
              />
            </div>
          </div>
        </DialogTitle>
        <DialogContent className="manageAssignmentsTable">
          <div className="manageAssignmentsTable">
            <TableContainer component={Paper}>
              <StyledTable aria-label="customized table">
                <TableBody className="assignmentsBodySection">
                  {manageAssignmentsDetails &&
                    manageAssignmentsDetails.map((item: IManageAssignments, id: number) => (
                      <StyledTableRow key={id}>
                        <StyledTableCell component="td" scope="row" className="statusCell">
                          {item.digitalCopySent === true ? 'Sent' : 'Not Sent'}
                        </StyledTableCell>

                        {item.emailAddress && item.emailAddress.length > 0 ? (
                          <StyledTableCell component="td" scope="row" className="emailCell">
                            {item.emailAddress}
                          </StyledTableCell>
                        ) : (
                          <></>
                        )}

                        <StyledTableCell>
                          <DialogActions className="rowBtnAction notDownloadedAction">
                            <Button
                              color="secondary"
                              variant="contained"
                              className="buttonFillBlue"
                              onClick={() => updateEmail(item.id)}
                            >
                              <FaPaperPlane />
                              {item.digitalCopySent === true
                                ? props?.ResendButtonText.value
                                : props?.SaveAndSendLink.value}
                            </Button>
                          </DialogActions>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
          </div>

          {loadStatus === true && (
            <div className="resendDiv">
              {resendStatus === true ? (
                <>
                  <span className="resendSuccess">
                    {state?.sitecoreDictionaryData?.ResendSuccessfulMessage ||
                      'Link has been resent.'}
                  </span>
                </>
              ) : (
                <>
                  <span className="resendFailure">
                    {state?.sitecoreDictionaryData?.ResendUnsuccessfulMessage ||
                      'Failure on resending the link. Please try again.'}
                  </span>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAssignments;
